import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Button, 
  Form, 
  Alert, 
  Table, 
  Badge,
  Modal,
  Spinner
} from 'react-bootstrap';
import { LuCloud, LuRefreshCw, LuSettings, LuCheck, LuX } from 'react-icons/lu';
import api from '../config/api';
import './AWSS3.scss';

const AWSS3 = () => {
  const [config, setConfig] = useState({
    aws_access_key_id: '',
    aws_secret_access_key: '',
    region_name: 'us-east-1',
    bucket_name: ''
  });
  
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncResults, setSyncResults] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    loadSyncStatus();
  }, []);

  const loadSyncStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/aws/sync-status');
      setSyncStatus(response.data);
    } catch (err) {
      console.error('Error loading sync status:', err);
      setError('Failed to load sync status');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post('/api/aws/configure', config);
      
      if (response.data.success) {
        setSuccess('AWS S3 configured successfully!');
        setShowConfigModal(false);
        loadSyncStatus();
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      console.error('Error configuring AWS:', err);
      setError(err.response?.data?.detail || 'Failed to configure AWS S3');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      setSuccess(null);
      
              const response = await api.post('/api/aws/sync-recordings', {
                  prefix: 'vapi/call/'
                });
      
      setSyncResults(response.data);
      
      if (response.data.success) {
        setSuccess(`Sync completed successfully! ${response.data.new_recordings_created} new recordings created, ${response.data.existing_recordings_updated} updated.`);
      } else {
        setError(`Sync completed with errors: ${response.data.errors.join(', ')}`);
      }
      
      loadSyncStatus();
    } catch (err) {
      console.error('Error syncing recordings:', err);
      setError(err.response?.data?.detail || 'Failed to sync recordings');
    } finally {
      setSyncing(false);
    }
  };

  const testConnection = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/api/aws/test-connection');
      
      if (response.data.success) {
        setSuccess('AWS S3 connection test successful!');
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      console.error('Error testing connection:', err);
      setError(err.response?.data?.detail || 'Failed to test connection');
    } finally {
      setLoading(false);
    }
  };

  const loadRecordings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/aws/recordings');
      setRecordings(response.data);
    } catch (err) {
      console.error('Error loading recordings:', err);
      setError('Failed to load recordings');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <Container fluid className="aws-s3-container">
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>
              <LuCloud className="me-2" />
              AWS S3 Recordings
            </h2>
            <div>
              <Button 
                variant="outline-primary" 
                onClick={() => setShowConfigModal(true)}
                className="me-2"
              >
                <LuSettings className="me-1" />
                Configure
              </Button>
              <Button 
                variant="primary" 
                onClick={handleSync}
                disabled={syncing}
              >
                {syncing ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <LuRefreshCw className="me-1" />
                    Sync Recordings
                  </>
                )}
              </Button>
            </div>
          </div>

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {/* Sync Status Card */}
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Sync Status</h5>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center">
                  <Spinner animation="border" />
                </div>
              ) : syncStatus ? (
                <Row>
                  <Col md={3}>
                    <div className="text-center">
                      <h4>{syncStatus.total_recordings}</h4>
                      <p className="text-muted">Total Recordings</p>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center">
                      <h4>{syncStatus.recent_recordings}</h4>
                      <p className="text-muted">Recent Recordings</p>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center">
                      <h4>
                        {syncStatus.connection_status ? (
                          <LuCheck className="text-success" />
                        ) : (
                          <LuX className="text-danger" />
                        )}
                      </h4>
                      <p className="text-muted">Connection Status</p>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center">
                      <h6>{syncStatus.bucket_name}</h6>
                      <p className="text-muted">Bucket Name</p>
                    </div>
                  </Col>
                </Row>
              ) : (
                <p className="text-muted">No sync status available</p>
              )}
            </Card.Body>
          </Card>

          {/* Sync Results */}
          {syncResults && (
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Last Sync Results</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={2}>
                    <div className="text-center">
                      <h5>{syncResults.total_files_found}</h5>
                      <small className="text-muted">Files Found</small>
                    </div>
                  </Col>
                  <Col md={2}>
                    <div className="text-center">
                      <h5>{syncResults.files_processed}</h5>
                      <small className="text-muted">Files Processed</small>
                    </div>
                  </Col>
                  <Col md={2}>
                    <div className="text-center">
                      <h5 className="text-success">{syncResults.new_recordings_created}</h5>
                      <small className="text-muted">New Recordings</small>
                    </div>
                  </Col>
                  <Col md={2}>
                    <div className="text-center">
                      <h5 className="text-info">{syncResults.existing_recordings_updated}</h5>
                      <small className="text-muted">Updated</small>
                    </div>
                  </Col>
                  <Col md={2}>
                    <div className="text-center">
                      <h5 className="text-danger">{syncResults.errors.length}</h5>
                      <small className="text-muted">Errors</small>
                    </div>
                  </Col>
                  <Col md={2}>
                    <div className="text-center">
                      <h6>{syncResults.last_sync_datetime ? formatDate(syncResults.last_sync_datetime) : 'N/A'}</h6>
                      <small className="text-muted">Last Sync</small>
                    </div>
                  </Col>
                </Row>
                
                {syncResults.errors.length > 0 && (
                  <div className="mt-3">
                    <h6>Errors:</h6>
                    <ul className="text-danger">
                      {syncResults.errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}

          {/* S3 Recordings Table */}
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">S3 Recordings</h5>
              <Button variant="outline-secondary" size="sm" onClick={loadRecordings}>
                <LuRefreshCw className="me-1" />
                Refresh
              </Button>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center">
                  <Spinner animation="border" />
                </div>
              ) : recordings.length > 0 ? (
                <Table responsive striped>
                  <thead>
                    <tr>
                      <th>File Name</th>
                      <th>Size</th>
                      <th>Last Modified</th>
                      <th>Type</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recordings.map((recording, index) => (
                      <tr key={index}>
                        <td>{recording.file_name}</td>
                        <td>{formatFileSize(recording.size)}</td>
                        <td>{formatDate(recording.last_modified)}</td>
                        <td>
                          <Badge bg={recording.file_extension === '.json' ? 'primary' : 'secondary'}>
                            {recording.file_extension}
                          </Badge>
                        </td>
                        <td>
                          <Button 
                            variant="outline-primary" 
                            size="sm"
                            href={recording.download_url}
                            target="_blank"
                          >
                            Download
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted text-center">No recordings found</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Configuration Modal */}
      <Modal show={showConfigModal} onHide={() => setShowConfigModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>AWS S3 Configuration</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleConfigSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>AWS Access Key ID</Form.Label>
              <Form.Control
                type="text"
                value={config.aws_access_key_id}
                onChange={(e) => setConfig({...config, aws_access_key_id: e.target.value})}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>AWS Secret Access Key</Form.Label>
              <Form.Control
                type="password"
                value={config.aws_secret_access_key}
                onChange={(e) => setConfig({...config, aws_secret_access_key: e.target.value})}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Region</Form.Label>
              <Form.Select
                value={config.region_name}
                onChange={(e) => setConfig({...config, region_name: e.target.value})}
              >
                <option value="us-east-1">US East (N. Virginia)</option>
                <option value="us-west-2">US West (Oregon)</option>
                <option value="eu-west-1">Europe (Ireland)</option>
                <option value="eu-west-2">Europe (London)</option>
                <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
              </Form.Select>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>S3 Bucket Name</Form.Label>
              <Form.Control
                type="text"
                value={config.bucket_name}
                onChange={(e) => setConfig({...config, bucket_name: e.target.value})}
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowConfigModal(false)}>
              Cancel
            </Button>
            <Button variant="outline-primary" onClick={testConnection} disabled={loading}>
              Test Connection
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? <Spinner animation="border" size="sm" /> : 'Save Configuration'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default AWSS3; 